import {
  Activity,
  BadgeCheck,
  BookOpenCheck,
  ClipboardCheck,
  FileOutput,
  FileStack,
  FileWarning,
  FolderCheck,
  Gauge,
  GitCompareArrows,
  LayoutDashboard,
  NotebookTabs,
  QrCode,
  Scale,
  ShieldAlert,
  X,
  Building2,
} from "lucide-react";
import type { WorkspaceMetrics } from "../types/domain";

export type Section =
  | "overview"
  | "project"
  | "contractors"
  | "duties"
  | "risks"
  | "hirarc"
  | "controls"
  | "incidents"
  | "documents"
  | "deliverables"
  | "changes"
  | "field"
  | "competence"
  | "approvals"
  | "evidence"
  | "reports";

interface ShellProps {
  section: Section;
  onSectionChange: (section: Section) => void;
  open: boolean;
  metrics: WorkspaceMetrics;
}

const navigation = [
  { id: "overview", label: "Assurance overview", icon: LayoutDashboard },
  { id: "project", label: "Project setup", icon: FileStack },
  { id: "contractors", label: "Supply Chain Risk", icon: Building2 },
  { id: "duties", label: "Regulation engine", icon: ClipboardCheck },
  { id: "risks", label: "Design risks", icon: Gauge },
  { id: "hirarc", label: "HIRARC", icon: Activity },
  { id: "controls", label: "Critical controls", icon: ShieldAlert },
  { id: "incidents", label: "Incident intelligence", icon: FileWarning },
  { id: "documents", label: "Revisions & files", icon: GitCompareArrows },
  { id: "deliverables", label: "CDM deliverables", icon: NotebookTabs },
  { id: "changes", label: "Regulatory change", icon: Scale },
  { id: "field", label: "Field verification", icon: QrCode },
  { id: "competence", label: "Competence", icon: BookOpenCheck },
  { id: "approvals", label: "Approvals", icon: BadgeCheck },
  { id: "evidence", label: "Evidence register", icon: FolderCheck },
  { id: "reports", label: "Controlled outputs", icon: FileOutput },
] satisfies { id: Section; label: string; icon: typeof LayoutDashboard }[];

export function Shell({ section, onSectionChange, open, metrics }: ShellProps) {
  return (
    <>
      {open && <button className="sidebar-scrim" onClick={() => onSectionChange(section)} />}
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="brand-row">
          <div className="brand-mark" aria-hidden="true">
            <span>DS</span>
          </div>
          <div className="brand-copy">
            <strong>Design Safety</strong>
            <span>MY · by OSHKKP</span>
          </div>
          <button
            className="sidebar-close"
            type="button"
            aria-label="Close navigation"
            onClick={() => onSectionChange(section)}
          >
            <X size={20} />
          </button>
        </div>

        <div className="workspace-label">Project workspace</div>
        <nav className="sidebar-nav" aria-label="Project navigation">
          {navigation.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={section === id ? "active" : ""}
              onClick={() => onSectionChange(id)}
            >
              <Icon size={19} strokeWidth={1.8} />
              <span>{label}</span>
              {id === "risks" && metrics.unapprovedRisks > 0 && (
                <em>{metrics.unapprovedRisks}</em>
              )}
              {id === "evidence" && metrics.openDutyEvidence > 0 && (
                <em>{metrics.openDutyEvidence}</em>
              )}
              {id === "hirarc" && metrics.hirarcHighRisks > 0 && (
                <em>{metrics.hirarcHighRisks}</em>
              )}
              {id === "controls" && metrics.controlsDue > 0 && (
                <em>{metrics.controlsDue}</em>
              )}
              {id === "incidents" && metrics.openIncidents > 0 && (
                <em>{metrics.openIncidents}</em>
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-assurance">
          <span className="assurance-ring" style={{ "--progress": metrics.evidenceCoverage } as React.CSSProperties}>
            <strong>{metrics.evidenceCoverage}%</strong>
          </span>
          <div>
            <small>Evidence maturity</small>
            <strong>
              {metrics.evidenceCoverage >= 80
                ? "Review ready"
                : metrics.evidenceCoverage >= 45
                  ? "Developing"
                  : "Early stage"}
            </strong>
          </div>
        </div>

        <div className="sidebar-footer">
          <span>CONTROLLED ASSURANCE</span>
          <small>Decisions remain accountable to named people</small>
        </div>
      </aside>
    </>
  );
}
